import React from 'react';
import { CheckCircle2, Circle, AlertCircle, Settings, Mail, Phone, MessageSquare, Bot, Zap, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useAIState } from '../contexts/AIStateContext';

/**
 * AI Setup Checklist Component
 * 
 * QA-APPROVED: Displays truthful setup status for AI execution channels.
 * Enterprise tone, no hype, fail visibly not silently.
 */

// Channel icons mapping
const CHANNEL_ICONS = {
  email: Mail,
  sms: MessageSquare,
  voice: Phone,
  chat: MessageSquare,
};

/**
 * Channel Status Badge
 */
export const ChannelStatusBadge = ({ channel, showLabel = true }) => {
  const Icon = CHANNEL_ICONS[channel.id] || Circle;
  const isReady = channel.ready;
  
  return (
    <div 
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm",
        isReady 
          ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400" 
          : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
      )}
      data-testid={`channel-status-${channel.id}`}
    >
      <Icon className="h-4 w-4" />
      {showLabel && <span>{channel.label}</span>}
      {isReady ? (
        <CheckCircle2 className="h-4 w-4 ml-auto" />
      ) : (
        <AlertCircle className="h-4 w-4 ml-auto" />
      )}
    </div>
  );
};

/**
 * Setup Checklist Item
 */
export const ChecklistItem = ({ item }) => {
  const isCompleted = item.completed;
  
  return (
    <div 
      className={cn(
        "flex items-center gap-3 py-2",
        isCompleted ? "text-foreground" : "text-muted-foreground"
      )}
      data-testid={`checklist-item-${item.id}`}
    >
      {isCompleted ? (
        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
      ) : (
        <Circle className="h-5 w-5 text-muted-foreground/50 flex-shrink-0" />
      )}
      <span className={cn(isCompleted && "line-through opacity-70")}>
        {item.label}
      </span>
      {item.required && !isCompleted && (
        <span className="text-xs text-amber-600 dark:text-amber-400 ml-auto">Required</span>
      )}
    </div>
  );
};

/**
 * AI Setup Checklist Card
 * 
 * Displays:
 * - Setup progress
 * - Channel readiness status
 * - Next required steps
 */
export const AISetupChecklist = ({ 
  integrationStatus, 
  compact = false,
  showHeader = true,
  isDemo = false,
  effectiveMode = 'SIMULATED',
  hasActiveSubscription = false,
  className 
}) => {
  const { planAiEnabled, aiStatusLoaded } = useAIState();
  // Manual Pro (no-AI plan): AI execution isn't part of this plan — hide the checklist.
  if (aiStatusLoaded && planAiEnabled === false) return null;
  if (!integrationStatus) {
    return null;
  }

  const { channels, setup_checklist, overall_ready } = integrationStatus;
  
  // 🔒 CRITICAL: Use effectiveMode from props (AIStateContext), NOT from integrationStatus
  // The integrationStatus.execution_mode may be stale or incorrect
  const execution_mode = effectiveMode;
  const completedCount = setup_checklist?.filter(item => item.completed).length || 0;
  const totalCount = setup_checklist?.length || 0;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Get channel array for display
  const channelArray = Object.entries(channels || {}).map(([id, data]) => ({
    id,
    ...data
  }));

  if (compact) {
    return (
      <div className={cn("space-y-3", className)} data-testid="ai-setup-checklist-compact">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">AI Setup Progress</span>
          <span className="font-medium">{progress}%</span>
        </div>
        <div className="h-2 bg-secondary rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full transition-all duration-300",
              overall_ready ? "bg-green-500" : "bg-amber-500"
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {channelArray.map(channel => (
            <ChannelStatusBadge key={channel.id} channel={channel} showLabel={false} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <Card className={cn("", className)} data-testid="ai-setup-checklist">
      {showHeader && (
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Settings className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">AI Setup Checklist</CardTitle>
                <CardDescription className="text-xs">
                  {overall_ready 
                    ? `Execution channels configured (${execution_mode || 'SIMULATED'})` 
                    : "Complete setup to enable AI execution"}
                </CardDescription>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold">{progress}%</span>
              <p className="text-xs text-muted-foreground">{completedCount}/{totalCount} complete</p>
            </div>
          </div>
        </CardHeader>
      )}
      <CardContent className={cn(!showHeader && "pt-4")}>
        {/* Progress Bar */}
        <div className="h-2 bg-secondary rounded-full overflow-hidden mb-4">
          <div 
            className={cn(
              "h-full transition-all duration-300",
              overall_ready ? "bg-green-500" : "bg-amber-500"
            )}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Channel Status */}
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Execution Channels
          </p>
          <div className="grid grid-cols-2 gap-2">
            {channelArray.map(channel => (
              <ChannelStatusBadge key={channel.id} channel={channel} />
            ))}
          </div>
        </div>

        {/* Checklist Items */}
        <div className="border-t pt-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Setup Steps
          </p>
          <div className="space-y-1">
            {setup_checklist?.map(item => {
              // Make "Switch to LIVE Mode" clickable to billing page
              if (item.id === 'live_mode' && !item.completed) {
                return (
                  <div key={item.id} className="space-y-3">
                    {/* SIMULATED Mode Info Banner */}
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4" data-testid="simulated-mode-banner">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div>
                            <p className="font-semibold text-amber-600 dark:text-amber-400">
                              {hasActiveSubscription 
                                ? "Switch to LIVE Mode" 
                                : "Switch to LIVE Mode (No real actions in SIMULATED mode)"}
                            </p>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              {hasActiveSubscription 
                                ? "You can enable LIVE mode to send real emails, SMS, and calls."
                                : "AI actions are previews only. Upgrade to send real emails, SMS, and calls."}
                            </p>
                          </div>
                          {hasActiveSubscription ? (
                            <Link to="/settings/ai?tab=budget" className="block">
                              <Button 
                                className="bg-green-600 hover:bg-green-700 text-white font-medium"
                                size="sm"
                                data-testid="enable-live-mode-btn"
                              >
                                Enable LIVE Mode →
                              </Button>
                            </Link>
                          ) : (
                            <Link to="/billing?tab=plans" className="block">
                              <Button 
                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
                                size="sm"
                                data-testid="upgrade-to-starter-btn"
                              >
                                Upgrade to Starter ($8/mo) →
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return <ChecklistItem key={item.id} item={item} />;
            })}
          </div>
        </div>

        {/* Action Button */}
        {!overall_ready && (
          <div className="mt-4 pt-3 border-t">
            <Link to="/settings/ai">
              <Button variant="outline" size="sm" className="w-full gap-2">
                <Settings className="h-4 w-4" />
                Configure in AI Settings
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Execution Status Banner
 * 
 * QA-APPROVED: Replaces misleading "AI Ready" banner with truthful status.
 */
export const AIExecutionStatusBanner = ({ 
  integrationStatus, 
  agents = {},
  isDemo = false,
  effectiveMode = 'SIMULATED',  // 🔒 Use from context
  hasSubscription = false,  // RULE 4: Added subscription prop
  className 
}) => {
  const { planAiEnabled, aiStatusLoaded } = useAIState();
  // Manual Pro (no-AI plan): no AI execution status to show.
  if (aiStatusLoaded && planAiEnabled === false) return null;
  if (!integrationStatus) {
    return null;
  }

  const { channels, overall_ready } = integrationStatus;
  
  // 🔒 CRITICAL: Use effectiveMode from props (AIStateContext), NOT from integrationStatus
  const execution_mode = effectiveMode;
  
  // Count enabled agents
  const enabledAgentCount = Object.values(agents).filter(a => a.enabled).length;
  
  // Count ready channels (excluding chat which is always ready)
  const readyChannels = Object.entries(channels || {})
    .filter(([id, data]) => id !== 'chat' && data.ready)
    .map(([id, data]) => data.label);
  
  const pendingChannels = Object.entries(channels || {})
    .filter(([id, data]) => id !== 'chat' && !data.ready)
    .map(([id, data]) => data.label);

  // Determine banner state
  const hasChannelsConfigured = readyChannels.length > 0;
  const allChannelsConfigured = pendingChannels.length === 0;
  
  // RULE 4: Banner messaging based on state - truthful status
  let title, description, variant;
  
  if (allChannelsConfigured && execution_mode === 'LIVE') {
    // Only show "Fully Operational" when actually in LIVE mode
    title = "AI Fully Operational (LIVE)";
    description = `${enabledAgentCount} agent${enabledAgentCount !== 1 ? 's' : ''} active • All channels ready • Real execution enabled`;
    variant = "success";
  } else if (execution_mode === 'LIVE' && hasChannelsConfigured) {
    title = "AI Active (LIVE) — Partial Channel Configuration";
    description = `${enabledAgentCount} agent${enabledAgentCount !== 1 ? 's' : ''} active • ${readyChannels.join(', ')} ready • ${pendingChannels.join(', ')} pending`;
    variant = "warning";
  } else if (hasSubscription && execution_mode !== 'LIVE') {
    // RULE 4: Subscription active but still SIMULATED
    title = "Subscription Active — LIVE Mode Available";
    description = `${enabledAgentCount} agent${enabledAgentCount !== 1 ? 's' : ''} enabled in SIMULATED mode • Switch to LIVE to enable real execution`;
    variant = "info";
  } else if (hasChannelsConfigured) {
    // Has channels but no subscription and not LIVE
    title = "AI Agents Enabled — SIMULATED Mode";
    description = `${enabledAgentCount} agent${enabledAgentCount !== 1 ? 's' : ''} active • ${readyChannels.join(', ')} configured • AI actions are previews only. Upgrade to send real emails, SMS, and calls.`;
    variant = "warning";
  } else {
    // RULE 4: Default SIMULATED state
    title = "AI Agents Enabled — SIMULATED Mode";
    description = `${enabledAgentCount} agent${enabledAgentCount !== 1 ? 's' : ''} selected • AI actions are previews only. Upgrade to send real emails, SMS, and calls.`;
    variant = "info";
  }

  const variantStyles = {
    success: "border-green-200 bg-gradient-to-r from-green-50 to-transparent dark:border-green-800 dark:from-green-900/20",
    warning: "border-amber-200 bg-gradient-to-r from-amber-50 to-transparent dark:border-amber-800 dark:from-amber-900/20",
    info: "border-blue-200 bg-gradient-to-r from-blue-50 to-transparent dark:border-blue-800 dark:from-blue-900/20"
  };

  const iconStyles = {
    success: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    warning: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
    info: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
  };

  return (
    <Card className={cn(variantStyles[variant], className)} data-testid="ai-execution-status-banner">
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", iconStyles[variant])}>
              {variant === 'success' ? (
                <Zap className="h-6 w-6" />
              ) : (
                <Bot className="h-6 w-6" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{title}</h3>
                {variant === 'success' && (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                )}
                {variant === 'warning' && (
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Show Upgrade button when not in LIVE mode and no subscription */}
            {execution_mode !== 'LIVE' && !hasSubscription && (
              <Link to="/billing?tab=plans">
                <Button size="sm" className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="upgrade-starter-btn">
                  Upgrade to Starter ($8/mo)
                </Button>
              </Link>
            )}
            <Link to="/settings/ai">
              <Button variant="outline" size="sm" className="gap-2">
                <Settings className="h-4 w-4" />
                AI Settings
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AISetupChecklist;
